/**
 * Account Page
 * Real-time Bitfinex account information display
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Layout from '../components/Layout'
import * as accountService from '../services/account.service'
import { bitfinexApiKeysService } from '../services/bitfinex-api-keys.service'
import { SUPPORTED_CURRENCIES, CURRENCY_LABELS } from '../types/currency'

export default function AccountPage() {
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<number | null>(null)
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD')

  // Fetch Bitfinex API keys
  const { data: apiKeys = [], isLoading: isLoadingKeys } = useQuery({
    queryKey: ['bitfinex-api-keys'],
    queryFn: () => bitfinexApiKeysService.list(),
  })

  // Filter only active keys
  const activeApiKeys = apiKeys.filter((key) => key.status === 'active')

  // Default to first API key if none selected
  const apiKeyId = selectedApiKeyId || activeApiKeys[0]?.id

  // Fetch account summary
  const { data: summary, isLoading, refetch } = useQuery({
    queryKey: ['account', 'summary', apiKeyId, selectedCurrency],
    queryFn: () => accountService.getAccountSummary(apiKeyId!, selectedCurrency),
    enabled: !!apiKeyId,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Fetch detailed offers
  const { data: offersData } = useQuery({
    queryKey: ['account', 'offers', apiKeyId, selectedCurrency],
    queryFn: () => accountService.getOffers(apiKeyId!, selectedCurrency),
    enabled: !!apiKeyId,
    refetchInterval: 30000,
  })

  // Fetch detailed loans
  const { data: loansData } = useQuery({
    queryKey: ['account', 'loans', apiKeyId, selectedCurrency],
    queryFn: () => accountService.getLoans(apiKeyId!, selectedCurrency),
    enabled: !!apiKeyId,
    refetchInterval: 30000,
  })

  const formatCurrency = (value: number, decimals: number = 2) => {
    return value.toFixed(decimals)
  }

  const formatAPR = (value: number) => {
    return value.toFixed(2) + '%'
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-TW')
  }

  const formatDays = (days: number) => {
    const hours = Math.floor((days % 1) * 24)
    const fullDays = Math.floor(days)
    return fullDays > 0 ? `${fullDays}天 ${hours}時` : `${hours}時`
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Bitfinex 帳戶</h1>
          <p style={styles.subtitle}>即時帳戶資訊和市場數據</p>
        </div>

        {isLoadingKeys ? (
          <div style={styles.loading}>載入 API Keys...</div>
        ) : activeApiKeys.length === 0 ? (
          <div style={styles.error}>
            <p>尚未設定 Bitfinex API Key</p>
            <p style={styles.errorDetail}>請先到 Bitfinex 憑證頁面新增 API Key</p>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div style={styles.controls}>
              <div style={styles.controlGroup}>
                <label style={styles.label}>選擇 API Key</label>
                <select
                  style={styles.select}
                  value={apiKeyId || ''}
                  onChange={(e) => setSelectedApiKeyId(Number(e.target.value))}
                >
                  {activeApiKeys.map((key) => (
                    <option key={key.id} value={key.id}>
                      {key.label || `API Key #${key.id}`}
                    </option>
                  ))}
                </select>
              </div>

          <div style={styles.controlGroup}>
            <label style={styles.label}>選擇幣種</label>
            <select
              style={styles.select}
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
            >
              {SUPPORTED_CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {CURRENCY_LABELS[currency]}
                </option>
              ))}
            </select>
          </div>

          <button style={styles.refreshButton} onClick={() => refetch()}>
            🔄 刷新數據
          </button>
        </div>

        {isLoading ? (
          <div style={styles.loading}>載入中...</div>
        ) : summary?.success ? (
          <>
            {/* Summary Cards */}
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>💰</div>
                <div>
                  <p style={styles.summaryLabel}>總餘額</p>
                  <p style={styles.summaryValue}>
                    {formatCurrency(summary.data.summary.totalBalance)} {selectedCurrency}
                  </p>
                  <p style={styles.summarySubtext}>
                    可用: {formatCurrency(summary.data.summary.availableBalance)} {selectedCurrency}
                  </p>
                </div>
              </div>

              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>📊</div>
                <div>
                  <p style={styles.summaryLabel}>已放貸</p>
                  <p style={styles.summaryValue}>
                    {formatCurrency(summary.data.summary.totalLent)} {selectedCurrency}
                  </p>
                  <p style={styles.summarySubtext}>
                    利用率: {formatCurrency(summary.data.summary.utilizationRate)}%
                  </p>
                </div>
              </div>

              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>📈</div>
                <div>
                  <p style={styles.summaryLabel}>每日預估收益</p>
                  <p style={styles.summaryValue}>
                    {formatCurrency(summary.data.summary.estimatedDailyEarnings, 4)} {selectedCurrency}
                  </p>
                  <p style={styles.summarySubtext}>
                    年化: {formatCurrency(summary.data.summary.estimatedAnnualEarnings, 2)}{' '}
                    {selectedCurrency}
                  </p>
                </div>
              </div>

              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>🎯</div>
                <div>
                  <p style={styles.summaryLabel}>市場 FRR</p>
                  <p style={styles.summaryValue}>{formatAPR(summary.data.market.frrAPR)}</p>
                  <p style={styles.summarySubtext}>
                    日利率: {formatCurrency(summary.data.market.frr * 100, 4)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Wallets */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>錢包餘額</h2>
              <div style={styles.walletsGrid}>
                {summary.data.wallets.funding && (
                  <div style={styles.walletCard}>
                    <h3 style={styles.walletTitle}>Funding Wallet</h3>
                    <div style={styles.walletRow}>
                      <span style={styles.walletLabel}>餘額:</span>
                      <span style={styles.walletValue}>
                        {formatCurrency(summary.data.wallets.funding.balance)} {selectedCurrency}
                      </span>
                    </div>
                    <div style={styles.walletRow}>
                      <span style={styles.walletLabel}>可用:</span>
                      <span style={styles.walletValue}>
                        {formatCurrency(summary.data.wallets.funding.available)} {selectedCurrency}
                      </span>
                    </div>
                    <div style={styles.walletRow}>
                      <span style={styles.walletLabel}>未結算利息:</span>
                      <span style={styles.walletValue}>
                        {formatCurrency(summary.data.wallets.funding.unsettledInterest, 6)}{' '}
                        {selectedCurrency}
                      </span>
                    </div>
                  </div>
                )}

                {summary.data.wallets.exchange && (
                  <div style={styles.walletCard}>
                    <h3 style={styles.walletTitle}>Exchange Wallet</h3>
                    <div style={styles.walletRow}>
                      <span style={styles.walletLabel}>餘額:</span>
                      <span style={styles.walletValue}>
                        {formatCurrency(summary.data.wallets.exchange.balance)} {selectedCurrency}
                      </span>
                    </div>
                    <div style={styles.walletRow}>
                      <span style={styles.walletLabel}>可用:</span>
                      <span style={styles.walletValue}>
                        {formatCurrency(summary.data.wallets.exchange.available)} {selectedCurrency}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Active Offers */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>活躍掛單</h2>
                <div style={styles.sectionStats}>
                  <span style={styles.stat}>
                    數量: <strong>{summary.data.activity.offers.count}</strong>
                  </span>
                  <span style={styles.stat}>
                    總額: <strong>{formatCurrency(summary.data.activity.offers.totalAmount)}</strong> {selectedCurrency}
                  </span>
                  <span style={styles.stat}>
                    平均利率: <strong>{formatAPR(summary.data.activity.offers.averageAPR)}</strong>
                  </span>
                </div>
              </div>

              {offersData?.data.offers && offersData.data.offers.length > 0 ? (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>ID</th>
                        <th style={styles.th}>金額</th>
                        <th style={styles.th}>日利率</th>
                        <th style={styles.th}>年化收益</th>
                        <th style={styles.th}>期限</th>
                        <th style={styles.th}>創建時間</th>
                        <th style={styles.th}>狀態</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offersData.data.offers.map((offer) => (
                        <tr key={offer.id} style={styles.tr}>
                          <td style={styles.td}>{offer.id}</td>
                          <td style={styles.td}>{formatCurrency(offer.amount)}</td>
                          <td style={styles.td}>{formatCurrency(offer.rate * 100, 4)}%</td>
                          <td style={styles.td}>{formatAPR(offer.rateAPR)}</td>
                          <td style={styles.td}>{offer.period} 天</td>
                          <td style={styles.td}>{formatDate(offer.createdAt)}</td>
                          <td style={styles.td}>
                            <span style={styles.statusActive}>{offer.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={styles.emptyState}>目前沒有活躍掛單</div>
              )}
            </div>

            {/* Active Loans */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>活躍貸款</h2>
                <div style={styles.sectionStats}>
                  <span style={styles.stat}>
                    數量: <strong>{summary.data.activity.loans.count}</strong>
                  </span>
                  <span style={styles.stat}>
                    總額: <strong>{formatCurrency(summary.data.activity.loans.totalAmount)}</strong> {selectedCurrency}
                  </span>
                  <span style={styles.stat}>
                    平均利率: <strong>{formatAPR(summary.data.activity.loans.averageAPR)}</strong>
                  </span>
                </div>
              </div>

              {loansData?.data.loans && loansData.data.loans.length > 0 ? (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>ID</th>
                        <th style={styles.th}>金額</th>
                        <th style={styles.th}>年化收益</th>
                        <th style={styles.th}>已運行</th>
                        <th style={styles.th}>剩餘時間</th>
                        <th style={styles.th}>已計利息</th>
                        <th style={styles.th}>預估總利息</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loansData.data.loans.map((loan) => (
                        <tr key={loan.id} style={styles.tr}>
                          <td style={styles.td}>{loan.id}</td>
                          <td style={styles.td}>{formatCurrency(loan.amount)}</td>
                          <td style={styles.td}>{formatAPR(loan.rateAPR)}</td>
                          <td style={styles.td}>{formatDays(loan.elapsedDays)}</td>
                          <td style={styles.td}>{formatDays(loan.remainingDays)}</td>
                          <td style={{ ...styles.td, color: '#16a34a' }}>
                            +{formatCurrency(loan.accruedInterest, 6)}
                          </td>
                          <td style={styles.td}>
                            {formatCurrency(loan.estimatedTotalInterest, 6)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Loans Summary */}
                  <div style={styles.loansSummary}>
                    <div style={styles.loansSummaryRow}>
                      <span>已累積總利息:</span>
                      <strong style={{ color: '#16a34a' }}>
                        +{formatCurrency(loansData.data.summary.totalAccruedInterest, 6)} {selectedCurrency}
                      </strong>
                    </div>
                    <div style={styles.loansSummaryRow}>
                      <span>預估總利息:</span>
                      <strong>
                        {formatCurrency(loansData.data.summary.totalEstimatedInterest, 6)} {selectedCurrency}
                      </strong>
                    </div>
                    <div style={styles.loansSummaryRow}>
                      <span>預估每日收益:</span>
                      <strong>
                        {formatCurrency(loansData.data.summary.estimatedDailyEarnings, 6)} {selectedCurrency}
                      </strong>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={styles.emptyState}>目前沒有活躍貸款</div>
              )}
            </div>

            {/* Last Updated */}
            <div style={styles.footer}>
              <p style={styles.footerText}>
                最後更新: {formatDate(summary.data.timestamp)} | 自動刷新: 每 30 秒
              </p>
            </div>
          </>
        ) : (
          <div style={styles.error}>
            <p>無法載入帳戶資訊</p>
            <p style={styles.errorDetail}>請確認 API Key 有效且有正確權限</p>
          </div>
        )}
        </>
        )}
      </div>
    </Layout>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  controls: {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
  },
  select: {
    padding: '10px 16px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
    minWidth: '200px',
  },
  refreshButton: {
    padding: '10px 20px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    fontSize: '18px',
    color: '#666',
  },
  error: {
    textAlign: 'center',
    padding: '60px',
    backgroundColor: '#fee2e2',
    borderRadius: '10px',
    color: '#dc2626',
  },
  errorDetail: {
    fontSize: '14px',
    marginTop: '8px',
    color: '#991b1b',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  summaryCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  summaryIcon: {
    fontSize: '36px',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '6px',
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  summaryValue: {
    fontSize: '26px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '4px',
  },
  summarySubtext: {
    fontSize: '13px',
    color: '#999',
  },
  section: {
    marginBottom: '40px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#333',
  },
  sectionStats: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  stat: {
    fontSize: '14px',
    color: '#666',
  },
  walletsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  walletCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  walletTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb',
  },
  walletRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  walletLabel: {
    fontSize: '14px',
    color: '#666',
  },
  walletValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  tableWrapper: {
    backgroundColor: 'white',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontSize: '13px',
    fontWeight: '600',
    color: '#555',
    borderBottom: '2px solid #e5e7eb',
    textTransform: 'uppercase',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '14px',
    fontSize: '14px',
    color: '#333',
  },
  statusActive: {
    padding: '4px 12px',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  loansSummary: {
    padding: '20px 24px',
    backgroundColor: '#f9fafb',
    borderTop: '2px solid #e5e7eb',
  },
  loansSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontSize: '15px',
    color: '#333',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px',
    backgroundColor: 'white',
    borderRadius: '10px',
    color: '#999',
    fontSize: '15px',
  },
  footer: {
    textAlign: 'center',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
    marginTop: '40px',
  },
  footerText: {
    fontSize: '13px',
    color: '#999',
  },
}
